import { useBoolean } from '@/src/hooks/use-boolean';
import { Button } from '@workspace/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ProfileProjectsList } from '../../project/components/ProfileProjectsList';
import { useProfileProjects } from '@/src/hooks/use-fetch-projects';
import { linkProjectToBit } from '@/src/actions/bits';
import { IProject } from 'types/src/types';
import { ProjectCard } from '../../project/components/ProjectCard';
import { DeleteButton } from '../../profile/components';
import { cn } from '@workspace/ui/lib/utils';
import { Icon } from '@/src/components/ui';
import { useRouter } from 'next/navigation';
import { RatingStats } from '../../project/components/RatingStats';
import Image from 'next/image';

type Props = {
  userUniqueLink: string;
  bitId: number;
  project?: IProject | null;
  isReadonly?: boolean;
  isMyProfile?: boolean;
  linkedProject?: IProject | null | undefined;
};

export const LinkProject = ({
  userUniqueLink,
  bitId,
  project,
  isReadonly,
  isMyProfile,
  linkedProject,
}: Props) => {
  const isLoadingPage = useBoolean(false);
  const router = useRouter();
  const [isChosen, setIsChosen] = useState(project?.id ?? null);
  const [selectedProject, setSelectedProject] = useState<
    IProject | null | undefined
  >(project);

  const isOpen = useBoolean(false);
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useProfileProjects(userUniqueLink);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const allPages = data?.pages ?? [];
  const projects = allPages.flatMap((page) => page.projects);

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [loadMore]);

  const handleAddProject = async (projectId?: number | null) => {
    isLoadingPage.onTrue();
    if (projectId === null) {
      setIsChosen(null);
    }
    const { data: bit } = await linkProjectToBit(bitId, projectId ?? null);
    setSelectedProject(bit?.project ?? null);
    isOpen.onFalse();
    isLoadingPage.onFalse();
  };

  if (isReadonly) {
    return (
      <div
        className={cn('flex flex-col', {
          'bg-bg-gray rounded-[20px] pt-5': isMyProfile,
        })}
        onClick={() => {
          isLoadingPage.onTrue();
          router.push(`/project/${linkedProject?.slug}`);
          isLoadingPage.onFalse();
        }}
      >
        {isMyProfile ? (
          <>
            <div className="flex items-start justify-between px-5">
              <h3 className="text-lg! font-medium!">Linked Project</h3>
            </div>
            <hr className="border-t border-black/10 mt-3" />
          </>
        ) : (
          <h2 className="text-2xl! font-semibold! my-5">Linked project</h2>
        )}
        <div
          className={cn(
            'flex gap-3 p-4 rounded-b-2xl cursor-pointer hover:bg-black/5 transition-all duration-200 max-w-110',
            {
              'hover:shadow-lg rounded-2xl': !isMyProfile,
            },
          )}
        >
          <Image
            src={linkedProject?.coverImageUrl as string}
            width={80}
            height={80}
            alt={linkedProject?.title || 'No project'}
            className={cn('object-cover w-20 h-20')}
          />

          <div className="flex gap-2 flex-col">
            <div className="flex justify-between text-lg! font-semibold! max-w-80">
              <h3 className="w-fit max-w-4/5 truncate">
                {linkedProject?.title}
              </h3>
              <span className="">
                {linkedProject?.hasDownloadAccess ? (
                  <div
                    className={cn('flex items-center gap-2', {
                      'text-black/50': linkedProject?.hasDownloadAccess,
                    })}
                  >
                    ${linkedProject?.price}
                    <hr className="border-r border-gray-200 h-5" />
                    <span className="font-normal text-sm">Purchased</span>
                  </div>
                ) : linkedProject?.price ? (
                  `$${linkedProject.price}`
                ) : (
                  'Free'
                )}
              </span>
            </div>
            <p className="text-sm! text-black/60 font-light! max-w-80">
              {linkedProject?.shortDescription}
            </p>
            <div className="flex items-end justify-between text-sm text-black/60 max-w-80">
              <RatingStats
                average={linkedProject?.rating?.average ?? 0}
                total={linkedProject?.rating?.total ?? 0}
              />
              <span className="flex">
                {linkedProject?.systems.map((system, index) => (
                  <Icon
                    key={index}
                    name={system.toLowerCase()}
                    className="size-5 mr-1 text-black/40"
                  />
                ))}
              </span>
            </div>
          </div>
        </div>
        {!isMyProfile && <hr className="border-t border-black/10 mt-5" />}
      </div>
    );
  }

  return (
    <div className="p-5 bg-bg-gray rounded-[12px] flex justify-between mb-5">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3">
          <h3 className="text-lg! font-medium!">Link a Project</h3>
          <p className="text-base! text-black/60 font-light!">
            Link this Bit to its project page
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="px-4! w-fit"
          onClick={isOpen.onTrue}
        >
          {selectedProject ? 'Change project' : 'Choose project'}
        </Button>
        <Dialog open={isOpen.value} onOpenChange={isOpen.setValue}>
          <DialogContent className="sm:max-w-[505px]">
            <DialogHeader>
              <DialogTitle className="text-lg! font-medium!">
                Attach the project
              </DialogTitle>
              <DialogDescription className="text-sm! text-black/60 font-light! mb-5">
                Attach this Bit to a project
              </DialogDescription>
            </DialogHeader>
            <div className="p-3 flex-wrap gap-3 bg-bg-gray rounded-[20px] overflow-y-auto max-h-[600px] mt-5">
              <ProfileProjectsList
                projects={projects}
                loading={isLoading}
                hasNextPage={hasNextPage}
                loadMore={loadMore}
                loadMoreRef={loadMoreRef}
                isSmall={true}
                setIsChosen={setIsChosen}
                isChosen={isChosen}
              />
            </div>
            <DialogFooter className="flex justify-between! mt-5">
              <Button
                type="button"
                variant="outline"
                onClick={isOpen.onFalse}
                size="sm"
                className="px-10 bg-bg-gray"
              >
                Close
              </Button>
              <Button
                onClick={() => handleAddProject(isChosen)}
                size="sm"
                className="px-4"
                type="button"
              >
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {selectedProject && (
        <ProjectCard
          isSmall={true}
          id={selectedProject.id}
          title={selectedProject.title}
          shortDescription={selectedProject.shortDescription}
          price={selectedProject.price ?? null}
          slug={selectedProject.slug}
          systems={selectedProject.systems}
          coverImageUrl={selectedProject.coverImageUrl}
          isBit={true}
          rating={selectedProject.rating}
        >
          <DeleteButton
            className="group-hover flex cursor-pointer right-1 -top-8"
            onClick={() => handleAddProject(null)}
          />
        </ProjectCard>
      )}
    </div>
  );
};
